import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useLoginSession from '../hooks/useLoginSession';
import useConfig from '../hooks/useConfig';

export default function OperatorCallback() {
  const [searchParams] = useSearchParams();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [tokenExchangeComplete, setTokenExchangeComplete] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const { config } = useConfig();
  const gatewayBaseUrl = config?.operatorGatewayBaseUrl || null;

  const productToken = searchParams.get('productToken');
  const loginStatus = searchParams.get('loginStatus');

  const sessionData = useMemo(() => {
    if (!tokenExchangeComplete) return null;
    return {
      operator,
      attemptId,
      access_token: accessToken,
    };
  }, [operator, attemptId, accessToken, tokenExchangeComplete]);

  const { status: sessionStatus, error: sessionError } = useLoginSession(
    gatewayBaseUrl,
    sessionData,
  );

  useEffect(() => {
    // Retrieve operator and attemptId from sessionStorage
    const savedOperator = sessionStorage.getItem('operator');
    const savedAttemptId = sessionStorage.getItem('attemptId');
    setOperator(savedOperator);
    setAttemptId(savedAttemptId);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!productToken) {
      setError('No productToken received in the callback URL.');
      setLoading(false);
      return;
    }

    if (!loginStatus) {
      setError('No loginStatus received in the callback URL.');
      setLoading(false);
      return;
    }

    // Case-insensitive check for loginStatus
    const status = loginStatus.toLowerCase();
    if (status !== 'success') {
      setError(`Authentication failed: ${loginStatus}`);
      setLoading(false);
      return;
    }

    if (!gatewayBaseUrl) {
      // Config not loaded yet; wait before triggering useLoginSession.
      return;
    }

    // Decode the productToken and hand it to useLoginSession, which is the
    // single source of truth for the gateway session call.
    const decodedToken = decodeURIComponent(productToken);
    setAccessToken(decodedToken);
    setTokenExchangeComplete(true);

    return () => {
      cancelled = true;
    };
  }, [productToken, loginStatus, gatewayBaseUrl]);

  useEffect(() => {
    if (sessionStatus === 'success') {
      console.log('[login-session] success');
      sessionStorage.removeItem('attemptId');
      sessionStorage.removeItem('operator');
      setLoading(false);
    } else if (sessionStatus === 'error') {
      console.error('[login-session] error:', sessionError);
      setError(sessionError || 'Login session call failed');
      setLoading(false);
    }
  }, [sessionStatus, sessionError]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Exchanging product token for access token...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-gray-800 shadow-xl rounded-2xl p-8 max-w-lg w-full">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Authentication Failed</h2>
          <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-red-300 text-sm break-all">{error}</p>
          </div>
          <button
            onClick={() => {
              window.location.href = `funteron://auth-return?status=error&errorCode=${encodeURIComponent(error)}`;
            }}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-8 rounded-lg transition duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
      <div className="bg-gray-800 shadow-xl rounded-2xl p-8 max-w-2xl w-full">
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">Authentication Successful</h2>
          </div>
        </div>
        {operator && (
          <p className="text-gray-400 text-center mb-4">
            Signed in with <span className="text-green-400 font-semibold">{operator}</span>
          </p>
        )}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => {
              window.location.href = `funteron://auth-return?status=success`;
            }}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-8 rounded-lg transition duration-200"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
